import { Tooltip as MuiTooltip, tooltipClasses as MuiTooltipClasses, PopperProps, Stack } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { MUIStyledCommonProps } from '@mui/system';
import React, { forwardRef } from 'react';
import { SxProps } from '../../utils';
import { Button } from '../Button';
import { Typography } from '../Typography';

export interface TooltipClasses {
  arrow: string;
  popper: string;
  tooltip: string;
}

export const tooltipClasses: TooltipClasses = {
  arrow: MuiTooltipClasses.arrow,
  popper: MuiTooltipClasses.popper,
  tooltip: MuiTooltipClasses.tooltip,
};

interface TooltipActionButtonProps {
  label: string;
  onClick: () => void;
}
export interface TooltipProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'title'> {
  /**
   * Tooltip reference element.
   */
  children: React.ReactElement;
  /**
   * Tooltip content
   */
  Content: NonNullable<React.ReactNode>;
  /**
   * Tooltip can have a primary action and a secondary action.
   */
  actions?: {
    primaryButton: TooltipActionButtonProps;
    secondaryButton: TooltipActionButtonProps;
  };
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<TooltipClasses>;
  /**
   * The props used for each slot inside the Tooltip.
   * @default {}
   */
  componentsProps?: {
    arrow?: React.HTMLProps<HTMLSpanElement> & MUIStyledCommonProps;
    popper?: Partial<PopperProps>;
    tooltip?: React.HTMLProps<HTMLDivElement> & MUIStyledCommonProps;
    transition?: TransitionProps;
  };
  /**
   * The number of milliseconds to wait before showing the tooltip.
   * This prop won't impact the enter touch delay (`enterTouchDelay`).
   * @default 100
   */
  enterDelay?: number;
  /**
   * The number of milliseconds to wait before showing the tooltip when one was already recently opened.
   * @default 0
   */
  enterNextDelay?: number;
  /**
   * This prop is used to help implement the accessibility logic.
   * If you don't provide this prop. It falls back to a randomly generated id.
   */
  id?: string;
  /**
   * If `true`, the component is shown.
   */
  open?: boolean;
  /**
   * Tooltip placement.
   * @default 'bottom'
   */
  placement?: 'bottom' | 'bottom-start' | 'left' | 'right' | 'top';
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
  /**
   * Tooltip title
   */
  title?: string;
}

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(
  { title, Content, actions, ...props },
  ref
) {
  // need to set the aria-label correctly if only content is defined and content is a string
  const isTooltipContentString = !title && !actions && typeof Content === 'string';

  const tooltipContent = isTooltipContentString ? (
    Content
  ) : (
    <Stack
      spacing={1}
      padding={(theme) => {
        return actions ? theme.spacing(1) : theme.spacing(0);
      }}
    >
      {title && <Typography variant="h4">{title}</Typography>}
      {Content}
      {actions && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
          <Button fullWidth onClick={actions.primaryButton.onClick}>
            {actions.primaryButton.label}
          </Button>
          <Button fullWidth variant="secondary" onClick={actions.secondaryButton.onClick}>
            {actions.secondaryButton.label}
          </Button>
        </Stack>
      )}
    </Stack>
  );
  return <MuiTooltip title={tooltipContent} {...props} ref={ref} />;
});
